import React from "react";
import {
  CORRECTION_FORM_FIELDS,
  NEW_FORM_FIELDS,
  PanFormAttachment,
  PanFormAttachmentPage,
  PanFormField,
  PanFormValues,
  sliceToBoxes,
} from "./panFormFields";
import { NEW_FORM_PAGE1, NEW_FORM_PAGE2 } from "./assets/newFormAsset";
import {
  CORRECTION_FORM_PAGE1,
  CORRECTION_FORM_PAGE2,
} from "./assets/correctionFormAsset";

type Props = {
  applicationType: "NEW" | "CORRECTION";
  values: PanFormValues;
  /** Every uploaded document available to append — the image source per id. */
  attachments?: PanFormAttachment[];
  /** The appended A4 pages the operator built, in print order. */
  attachmentPages?: PanFormAttachmentPage[];
  /**
   * The acknowledgement receipt cut out of the acknowledgement PDF (a PNG
   * data-URL). Printed as the first page when `values.receiptPage` is "YES".
   */
  receiptImage?: string | null;
  /**
   * Preview only: makes the cross sign draggable. Called with each step of the
   * drag, in % of the page width / height. Omitted, the sign is inert.
   */
  onCrossSignMove?: (dx: number, dy: number) => void;
  /** Preview only: the same, for the signature in the declaration box. */
  onSigMove?: (dx: number, dy: number) => void;
};

/**
 * Drag the cross sign across its page. Deltas are reported per pointer move,
 * as % of the page (the sign's positioned parent), so the result is the same
 * whatever zoom the preview is shown at.
 */
const startCrossSignDrag = (
  e: React.PointerEvent<HTMLElement>,
  onMove: (dx: number, dy: number) => void
) => {
  const el = e.currentTarget;
  const page = el.parentElement;
  if (!page) return;
  const rect = page.getBoundingClientRect();
  let lastX = e.clientX;
  let lastY = e.clientY;
  e.preventDefault();
  el.setPointerCapture(e.pointerId);
  const move = (ev: PointerEvent) => {
    const dx = ((ev.clientX - lastX) / rect.width) * 100;
    const dy = ((ev.clientY - lastY) / rect.height) * 100;
    lastX = ev.clientX;
    lastY = ev.clientY;
    if (dx || dy) onMove(dx, dy);
  };
  const end = () => {
    el.removeEventListener("pointermove", move);
    el.removeEventListener("pointerup", end);
    el.removeEventListener("pointercancel", end);
  };
  el.addEventListener("pointermove", move);
  el.addEventListener("pointerup", end);
  el.addEventListener("pointercancel", end);
};

const renderField = (
  field: PanFormField,
  values: PanFormValues,
  idx: number,
  onCrossSignMove?: (dx: number, dy: number) => void,
  onSigMove?: (dx: number, dy: number) => void
) => {
  if (field.kind === "tick") {
    if (!field.on(values)) return null;
    // The tick is DRAWN, not typed. It used to be the ✓ character (U+2713),
    // which rendered in the browser preview but came out empty in the generated
    // PDF: the server's headless Chrome has no font containing that glyph, so
    // every tick on every form silently vanished. Two borders on a rotated box
    // need no font at all, so the mark is identical in the preview and in the
    // PDF on any machine.
    //
    // `top` / `left` are the CENTRE of the printed tick box (see
    // panFormFields.ts); `translate(-50%, -50%)` centres the mark's own box on
    // that point, so it always lands inside the box.
    const size = field.fontSize || 12;
    return (
      <span
        key={idx}
        style={{
          position: "absolute",
          top: `${field.top}%`,
          left: `${field.left}%`,
          transform: "translate(-50%, -50%)",
          width: `${size}px`,
          height: `${size * 0.5}px`,
          display: "block",
        }}
      >
        <span
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            // An "L" of two borders becomes a check mark once rotated. Kept at
            // 1.8px so it stays crisp after printing and scanning without
            // going heavy like the old bold glyph.
            borderLeft: "1.8px solid #000",
            borderBottom: "1.8px solid #000",
            transform: "rotate(-45deg)",
          }}
        />
      </span>
    );
  }

  // Signature across the left photo. Not clipped, so it can run past the
  // photo's edge onto the form; the cleaned signature is ink on transparent
  // paper, so the photo shows between the strokes.
  if (field.kind === "crossSign") {
    const src = (values[field.key] as string) || "";
    if (!src || values.crossSign !== "YES") return null;
    const num = (v: string, fallback: number) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : fallback;
    };
    const scale = num(values.crossSignSize, 100) / 100;
    const rotate = (field.rotate || 0) + num(values.crossSignTilt, 0);
    // Resized about its centre, so "bigger" does not also mean "moves right".
    const width = field.width * scale;
    const height = field.height * scale;
    const top = field.top + num(values.crossSignDy, 0) - (height - field.height) / 2;
    const left = field.left + num(values.crossSignDx, 0) - (width - field.width) / 2;
    return (
      <span
        key={idx}
        style={{
          position: "absolute",
          top: `${top}%`,
          left: `${left}%`,
          width: `${width}%`,
          height: `${height}%`,
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: rotate ? `rotate(${rotate}deg)` : undefined,
          pointerEvents: onCrossSignMove ? "auto" : "none",
          cursor: onCrossSignMove ? "move" : undefined,
          touchAction: onCrossSignMove ? "none" : undefined,
        }}
        title={onCrossSignMove ? "Drag to move the signature" : undefined}
        onPointerDown={
          onCrossSignMove ? (e) => startCrossSignDrag(e, onCrossSignMove) : undefined
        }
      >
        <img
          src={src}
          draggable={false}
          alt="Cross signature"
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            // A signature the ink-cleaning pass could not clean still carries
            // its paper; multiply drops white paper out over the photo too.
            mixBlendMode: "multiply",
          }}
        />
      </span>
    );
  }

  // Signature / thumb-impression image, overlaid in the signature box. The
  // value is a base64 data-URL, so it embeds directly in the posted HTML and
  // Puppeteer prints it without any upload/storage step.
  if (field.kind === "image") {
    const src = (values[field.key] as string) || "";
    if (!src) return null;
    // The passport photograph fits its box, inset a few px from the printed
    // border. It used to go through the signature path below, whose 70% height
    // cap printed a small photo floating in a mostly empty box.
    //
    // `contain`, not `cover`: the crop is free, so the photo is rarely the
    // box's exact 3.5:4.5. `cover` filled the box by cutting off whatever did
    // not fit, which on a tall crop was the top of the applicant's head. The
    // whole crop now prints, centred, with a thin margin on the short side.
    if (field.key === "photo") {
      return (
        <span
          key={idx}
          style={{
            position: "absolute",
            top: `${field.top}%`,
            left: `${field.left}%`,
            width: `${field.width}%`,
            height: `${field.height}%`,
            boxSizing: "border-box",
            padding: "6px",
            overflow: "hidden",
          }}
        >
          <img
            src={src}
            alt="Photograph"
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </span>
      );
    }
    // The box is a positioned, CLIPPING container and the image is centred
    // inside it. Sizing the <img> itself (the old approach) meant a signature
    // whose aspect ratio did not match the box was laid out from the box's
    // top-left corner and could ride outside the printed brackets; `overflow:
    // hidden` now makes that impossible, whatever is pasted in.
    //
    // The container is the printed bracket EXACTLY, borders included, so it is
    // inset by a couple of px: the signature then fills the box without any
    // stroke touching or crossing the printed rule. The image reaching here has
    // normally been trimmed to the strokes already — automatically for the
    // prefilled signature (cleanSignatureImage) and on demand for a pasted one
    // (ImageCropInput's "Ink only" tick) — so what is centred here is the
    // signature itself rather than the slip it was photographed on.
    //
    // The operator can nudge it (move / size / tilt, `sig*` values) from the
    // preview, the same way as the cross sign.
    const num = (v: string, fallback: number) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : fallback;
    };
    const scale = num(values.sigSize, 100) / 100;
    const tilt = num(values.sigTilt, 0);
    return (
      <span
        key={idx}
        style={{
          position: "absolute",
          top: `${field.top + num(values.sigDy, 0)}%`,
          left: `${field.left + num(values.sigDx, 0)}%`,
          width: `${field.width}%`,
          height: `${field.height}%`,
          boxSizing: "border-box",
          padding: "3px 6px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: onSigMove ? "auto" : "none",
          cursor: onSigMove ? "move" : undefined,
          touchAction: onSigMove ? "none" : undefined,
        }}
        title={onSigMove ? "Drag to move the signature" : undefined}
        onPointerDown={onSigMove ? (e) => startCrossSignDrag(e, onSigMove) : undefined}
      >
        {/* A fixed INNER box (78% x 70% of the bracket, centred) is the
            signature's boundary. The caps used to be % of the outer bracket,
            so with the Size control raised (up to 250%) they reached 100% and
            a long signature ran edge to edge across the printed box. Now the
            Size control scales the signature only within this inner box, and
            the box clips, so it can never grow past it. At 100% size the
            caps (90% x 79% of the inner box) give the same size as before. */}
        <span
          style={{
            width: "78%",
            height: "70%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transform: tilt ? `rotate(${tilt}deg)` : undefined,
          }}
        >
          {/* width / height, not max-width / max-height: a max only ever
              shrinks, so a small (low-resolution) signature printed at its
              own pixel size — tiny in the box — and the Size control could
              not make it any bigger. `contain` now scales it up or down to
              the box, keeping its shape. */}
          <img
            src={src}
            alt="Signature"
            draggable={false}
            style={{
              width: `${Math.min(100, 90 * scale)}%`,
              height: `${Math.min(100, 79 * scale)}%`,
              objectFit: "contain",
            }}
          />
        </span>
      </span>
    );
  }

  const raw = (values[field.key] as string) || "";

  if (field.kind === "text") {
    const text = field.uppercase ? raw.toUpperCase() : raw;
    const value = (
      <span
        key={idx}
        style={{
          position: "absolute",
          top: `${field.top}%`,
          left: `${field.left}%`,
          width: field.width ? `${field.width}%` : undefined,
          textAlign: field.align || "left",
          fontSize: `${field.fontSize || 11}px`,
          lineHeight: 1,
          color: "#000",
          fontFamily: "Arial, sans-serif",
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        {text}
      </span>
    );

    if (!field.rule || field.ruleTop === undefined) return value;

    // The rule is its own element rather than a borderBottom on the value:
    // the value's box top is the baseline MINUS the baseline drop, so a border
    // on it would land below the printed leader and read as a second line. Sat
    // on `ruleTop` — the baseline itself — ours continues the printed dots
    // instead of doubling them. It is drawn first so the value paints over it.
    return (
      <React.Fragment key={idx}>
        <span
          style={{
            position: "absolute",
            top: `${field.ruleTop}%`,
            left: `${field.left}%`,
            width: field.width ? `${field.width}%` : undefined,
            borderTop: "1px dotted #000",
          }}
        />
        {value}
      </React.Fragment>
    );
  }

  // grid: one character per box. Each character is its own absolutely
  // positioned span so its width/position resolve against the A4 page
  // (the positioned page ancestor) rather than a shrink-to-fit row.
  //
  // A multi-row grid (the three-row Aadhaar name block) draws one row per field,
  // so each row picks its own slice of the value — filled continuously, box after
  // box, with no break at word boundaries.
  const source =
    field.line === undefined
      ? raw
      : sliceToBoxes(raw, field.maxChars || raw.length, field.lines || 1)[field.line] || "";
  const sliced = field.slice ? source.slice(field.slice[0], field.slice[1]) : source;
  const chars = sliced
    .toUpperCase()
    .slice(0, field.maxChars || sliced.length)
    .split("");
  return (
    <React.Fragment key={idx}>
      {chars.map((ch, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            top: `${field.top}%`,
            left: `${field.left + i * field.pitch}%`,
            width: `${field.pitch}%`,
            textAlign: "center",
            fontSize: `${field.fontSize || 11}px`,
            // Same face and weight as the affidavit/receipt PDF. Each character
            // is centred in its own box, so a monospace face bought nothing —
            // and 'Courier New' at weight 600 printed heavy enough that the
            // strokes bled together once the form was scanned back in.
            fontFamily: "Arial, Helvetica, sans-serif",
            fontWeight: 400,
            lineHeight: 1,
            color: "#000",
          }}
        >
          {ch === " " ? " " : ch}
        </span>
      ))}
    </React.Fragment>
  );
};

// One A4 page: background image + the overlay fields belonging to that page.
// `page-break-after` makes Puppeteer emit a real page break between them; the
// last page omits it so no trailing blank page is printed.
const FormPage = ({
  bg,
  fields,
  values,
  isLast,
  onCrossSignMove,
  onSigMove,
}: {
  bg: string;
  fields: PanFormField[];
  values: PanFormValues;
  isLast: boolean;
  onCrossSignMove?: (dx: number, dy: number) => void;
  onSigMove?: (dx: number, dy: number) => void;
}) => (
  <div
    style={{
      position: "relative",
      width: "210mm",
      margin: "auto",
      background: "#fff",
      pageBreakAfter: isLast ? "auto" : "always",
      breakAfter: isLast ? "auto" : "page",
    }}
  >
    <img src={bg} alt="PAN Form" style={{ width: "100%", display: "block" }} />
    {fields.map((field, idx) => renderField(field, values, idx, onCrossSignMove, onSigMove))}
  </div>
);

// One appended A4 page holding one to four uploaded documents. Each document
// gets an equal share of the page and is scaled to fit inside it, so mixing
// portrait and landscape scans on one sheet never distorts anything.
//
// "2x2" lays the slots out as a two-column grid; every other layout stacks them
// in a single column.
const AttachmentPage = ({
  page,
  attachments,
  isLast,
}: {
  page: PanFormAttachmentPage;
  attachments: PanFormAttachment[];
  isLast: boolean;
}) => {
  const items = page.items
    .map((id) => attachments.find((a) => a.id === id))
    .filter(Boolean) as PanFormAttachment[];
  const isGrid = page.layout === "2x2";

  return (
    <div
      style={{
        position: "relative",
        width: "210mm",
        height: "297mm",
        margin: "auto",
        background: "#fff",
        boxSizing: "border-box",
        padding: "6mm",
        display: isGrid ? "grid" : "flex",
        gridTemplateColumns: isGrid ? "1fr 1fr" : undefined,
        gridTemplateRows: isGrid ? "1fr 1fr" : undefined,
        flexDirection: isGrid ? undefined : "column",
        gap: "4mm",
        overflow: "hidden",
        pageBreakAfter: isLast ? "auto" : "always",
        breakAfter: isLast ? "auto" : "page",
      }}
    >
      {items.map((a) => (
        <div
          key={a.id}
          style={{
            // Each slot is an equal share of the page; `minHeight: 0` stops a
            // tall image from stretching its flex row past that share.
            flex: isGrid ? undefined : "1 1 0",
            minHeight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <img
            src={a.edited || a.url}
            alt={a.label}
            crossOrigin="anonymous"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              // Attached documents print black & white. A cropped one already
              // carries the operator's Grayscale choice (100% unless they slid
              // it down); one attached without cropping is greyed here.
              filter: a.edited ? undefined : "grayscale(100%)",
            }}
          />
        </div>
      ))}
    </div>
  );
};

// The acknowledgement receipt, full page width at the top of its own A4 sheet —
// the receipt is about half a page tall, and the rest stays blank.
const ReceiptPage = ({ src, isLast }: { src: string; isLast: boolean }) => (
  <div
    style={{
      position: "relative",
      width: "210mm",
      height: "297mm",
      margin: "auto",
      background: "#fff",
      boxSizing: "border-box",
      padding: "8mm",
      overflow: "hidden",
      pageBreakAfter: isLast ? "auto" : "always",
      breakAfter: isLast ? "auto" : "page",
    }}
  >
    <img
      src={src}
      alt="Acknowledgement receipt"
      style={{ width: "100%", display: "block" }}
    />
  </div>
);

const PanFormTemplate = ({
  applicationType,
  values,
  attachments = [],
  attachmentPages = [],
  receiptImage = null,
  onCrossSignMove,
  onSigMove,
}: Props) => {
  const isNew = applicationType === "NEW";
  const pages = isNew
    ? [NEW_FORM_PAGE1, NEW_FORM_PAGE2]
    : [CORRECTION_FORM_PAGE1, CORRECTION_FORM_PAGE2];
  const allFields = isNew ? NEW_FORM_FIELDS : CORRECTION_FORM_FIELDS;

  // A page with no resolvable document would print as a blank sheet.
  const printedPages = attachmentPages.filter((p) =>
    p.items.some((id) => attachments.some((a) => a.id === id))
  );
  // The page-break flag must sit on the true final page of the document — if a
  // form page kept `pageBreakAfter: auto` while attachments follow, the break
  // before the first attachment would be swallowed and two pages would collide.
  const hasReceipt = values.receiptPage === "YES" && !!receiptImage;
  const offset = hasReceipt ? 1 : 0;
  const totalPages = offset + pages.length + printedPages.length;

  return (
    <div id="pdfContent" style={{ width: "210mm", margin: "auto", background: "#fff" }}>
      {hasReceipt && (
        <ReceiptPage src={receiptImage as string} isLast={totalPages === 1} />
      )}
      {pages.map((bg, pageIdx) => {
        const pageNo = pageIdx + 1;
        const pageFields = allFields.filter((f) => (f.page ?? 1) === pageNo);
        return (
          <FormPage
            key={pageNo}
            bg={bg}
            fields={pageFields}
            values={values}
            isLast={offset + pageNo === totalPages}
            onCrossSignMove={onCrossSignMove}
            onSigMove={onSigMove}
          />
        );
      })}
      {printedPages.map((p, i) => (
        <AttachmentPage
          key={p.id}
          page={p}
          attachments={attachments}
          isLast={offset + pages.length + i + 1 === totalPages}
        />
      ))}
    </div>
  );
};

export default PanFormTemplate;
